import { cn } from "@/lib/utils.ts";
import type { ArtifactComponent, LeafComponent } from "@/types/artifacts-view.ts";
import type { ReactNode } from "react";

type HorizontalGroupComponentData = Extract<ArtifactComponent, { type: "horizontalGroup" }>;

/**
 * Renderer is injected as a prop rather than imported. Avoids the
 * `HorizontalGroupBlock <-> renderComponent` import cycle that arises when
 * a layout component needs to recurse into the dispatcher that owns it.
 */
type RenderLeaf = (component: LeafComponent) => ReactNode;

const gapClass = (gap: HorizontalGroupComponentData["gap"]) => {
  // Static class names so the Tailwind JIT picks them up.
  switch (gap) {
    case "1":
      return "gap-1";
    case "3":
      return "gap-3";
    case "4":
      return "gap-4";
    case "2":
    default:
      return "gap-2";
  }
};

const alignClass = (align: HorizontalGroupComponentData["align"]) => {
  switch (align) {
    case "center":
      return "items-center";
    case "end":
      return "items-end";
    case "start":
    default:
      return "items-start";
  }
};

const justifyClass = (justify: HorizontalGroupComponentData["justify"]) => {
  switch (justify) {
    case "between":
      return "justify-between";
    case "end":
      return "justify-end";
    case "start":
    default:
      return "justify-start";
  }
};

export const HorizontalGroupBlock = ({
  component,
  renderLeaf,
}: {
  component: HorizontalGroupComponentData;
  renderLeaf: RenderLeaf;
}) => {
  // `wrap` keeps the row horizontal, allowing children to flow to a new line
  // on overflow. `stack` switches to a vertical column on small containers
  // and back to a row at the @md container-query breakpoint and above.
  const responsiveClass =
    component.responsive === "stack" ? "flex-col @md:flex-row" : "flex-row flex-wrap";

  return (
    <div
      className={cn(
        "flex",
        responsiveClass,
        gapClass(component.gap),
        alignClass(component.align),
        justifyClass(component.justify),
      )}
    >
      {component.children.map((child) => (
        <div key={child.id}>{renderLeaf(child)}</div>
      ))}
    </div>
  );
};
